import { TASK_REPORT_CHANNELS, type TaskReportChannel } from "./taskReportOrigin";

export const ACTIVITY_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const ACTIVITY_ENTITIES = ["issue", "doc", "note", "project", "progress_card", "comment", "project_milestone"] as const;
export const ACTIVITY_ACTIONS = ["create", "update", "delete", "comment", "status_change", "assign", "restore"] as const;
export type ActivityEntity = typeof ACTIVITY_ENTITIES[number];
export type ActivityAction = typeof ACTIVITY_ACTIONS[number];
export type ActivityFilters = { person?: string; project?: string; type?: ActivityEntity; action?: ActivityAction; origin?: TaskReportChannel };
export type ActivityCursor = { before: string; beforeId: string };
export type ActivityActor = { id: string; name: string; initials: string; color: string; isBot: boolean };
export type ActivityEvent = {
  id: string; entityType: ActivityEntity; entityId: string; action: ActivityAction;
  title: string; identifier: string | null; href: string | null; createdAt: string;
  actor: ActivityActor | null; projectId: string | null;
  beforeStatus: string | null; afterStatus: string | null;
  /** Recorded at creation, not the tool that performed a later update. */
  reportOrigin: TaskReportChannel | null;
};
export type ActivityPage = {
  items: ActivityEvent[]; cursor: ActivityCursor | null; timezone: string;
  people: ActivityActor[]; projects: { id: string; name: string }[];
};
export const activityActionLabel: Record<ActivityAction, string> = {
  create: "criou", update: "atualizou", delete: "removeu", comment: "comentou em",
  status_change: "mudou o estado de", assign: "alterou a atribuição de", restore: "restaurou",
};
export const activityEntityLabel: Record<ActivityEntity, string> = {
  issue: "Tarefa", doc: "Documento", note: "Nota", project: "Projeto",
  progress_card: "Relatório", comment: "Comentário", project_milestone: "Marco",
};
export function activityDay(timestamp: string, timezone: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric", timeZone: timezone }).format(new Date(timestamp));
}
export function parseActivityFilters(params: URLSearchParams): ActivityFilters | null {
  const result: ActivityFilters = {};
  for (const key of ["person", "project"] as const) {
    const value = params.get(key); if (!value) continue;
    if (!ACTIVITY_UUID.test(value)) return null; result[key] = value;
  }
  for (const [key, allowed] of [["type", ACTIVITY_ENTITIES], ["action", ACTIVITY_ACTIONS], ["origin", TASK_REPORT_CHANNELS]] as const) {
    const value = params.get(key); if (!value) continue;
    if (!(allowed as readonly string[]).includes(value)) return null;
    Object.assign(result, { [key]: value });
  }
  return result;
}
export function parseActivityCursor(params: URLSearchParams): ActivityCursor | null | false {
  const before = params.get("before"), beforeId = params.get("beforeId");
  if (!before && !beforeId) return null;
  return before && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(before) &&
    Number.isFinite(Date.parse(before)) && beforeId && ACTIVITY_UUID.test(beforeId)
    ? { before, beforeId } : false;
}
